import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";
import { usePrincipalKey } from "@auth/sessionStore";
import {
  CallContextPanel,
  formatMoney,
  SELECTED_CLIENT_NOTE,
} from "@notifications/CallContextPanel";
import { toPatientSignalment } from "@patients/patientsClient";
import { signalmentOf } from "@patients/signalment";
import { type CallContext, useCallContext } from "@phone/callContextClient";
import { phoneCallOutcome } from "@phone/phoneCallOutcome";
import {
  Button,
  HistoryItem,
  HistoryRail,
  PatientSprite,
  SkeletonStack,
  StickyNote,
} from "@scribenote/brian-design-system";
import { useOpenCall, useOpenPatient } from "@shell/navigation";
import { useQueries } from "@tanstack/react-query";
import { formatMonthDay } from "@utils/format-date";
import { useNow } from "@utils/use-now";
import type { ReactNode } from "react";

type ContextQuery = ReturnType<typeof useCallContext>;
type Patient = components["schemas"]["PatientOut"];
type Visit = components["schemas"]["TodayVisitOut"];
type Measurement = components["schemas"]["MeasurementOut"];

const PET_LIMIT = 6;
const HISTORY_LIMIT = 5;
const DAY_MS = 86_400_000;

const weekdayTime = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  hour: "numeric",
  minute: "2-digit",
});
const startOfDay = (ms: number) => new Date(ms).setHours(0, 0, 0, 0);

const visitTime = (visit: Visit) => visit.scheduled_start ?? visit.created_at;

const soonLabel = (iso: string, now: number) => {
  const at = new Date(iso);
  return at.getTime() - now < 6 * DAY_MS
    ? weekdayTime.format(at)
    : formatMonthDay(iso);
};

const dueLabel = (iso: string | null, now: number) => {
  if (!iso) return null;
  const due = startOfDay(new Date(iso).getTime());
  const today = startOfDay(now);
  if (due < today) return "overdue";
  if (due === today) return "due today";
  return `due ${formatMonthDay(iso)}`;
};

const lastDigits = (number: string | null | undefined) =>
  (number ?? "").replace(/\D/g, "").slice(-10);

const Heading = ({ children }: { children: ReactNode }) => (
  <h3 className="mt-4 mb-1.5 text-mono-caps text-fine-print text-annotation first:mt-0">
    {children}
  </h3>
);

const OpenItems = ({ data, now }: { data: CallContext; now: number }) => {
  const tasks = data.tasks.filter((task) => task.status === "open");
  const owing = data.invoices.filter(
    (invoice) =>
      invoice.balance_amount != null &&
      invoice.balance_amount > 0 &&
      (invoice.settlement_status === "unpaid" ||
        invoice.settlement_status === "partial"),
  );
  if (tasks.length === 0 && owing.length === 0) return null;
  return (
    <>
      <Heading>Open items</Heading>
      <ul className="divide-y divide-dotted divide-rule-strong rounded-paper border border-rule bg-tint-attention px-3">
        {tasks.map((task) => (
          <li key={task.uuid} className="py-2">
            <p className="text-sm font-medium text-ink">{task.title}</p>
            <p className="text-fine-print text-annotation">
              {["To-do", dueLabel(task.due_at, now)]
                .concat(task.severity === "urgent" ? ["Urgent"] : [])
                .filter(Boolean)
                .join(" · ")}
            </p>
          </li>
        ))}
        {owing.map((invoice) => (
          <li key={invoice.uuid} className="py-2">
            <p className="text-sm font-medium text-ink">
              {formatMoney(invoice.balance_amount, invoice.currency)} balance
            </p>
            <p className="text-fine-print text-annotation">
              {[
                invoice.lines[0]?.description ??
                  invoice.kind.replace(/_/g, " "),
                invoice.issued_at &&
                  `issued ${formatMonthDay(invoice.issued_at)}`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </li>
        ))}
      </ul>
    </>
  );
};

const weightLine = (weights: Measurement[] | undefined) => {
  const [latest, previous] = weights ?? [];
  if (!latest) return null;
  const now = Number(latest.value);
  const before = previous ? Number(previous.value) : NaN;
  const change =
    previous && previous.unit === latest.unit && Number.isFinite(before)
      ? Math.round((now - before) * 10) / 10
      : 0;
  return (
    <>
      {latest.value} {latest.unit}
      {change !== 0 && (
        <span className="text-annotation">
          {" "}
          {change > 0 ? "↑" : "↓"}
          {Math.abs(change)}
        </span>
      )}
    </>
  );
};

const PetFact = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="flex items-baseline justify-between gap-2 text-fine-print">
    <span className="text-annotation">{label}</span>
    <span className="font-mono tabular-nums text-ink">{children ?? "—"}</span>
  </div>
);

const PetCard = ({
  patient,
  visits,
  weights,
  now,
}: {
  patient: Patient;
  visits: Visit[];
  weights: Measurement[] | undefined;
  now: number;
}) => {
  const openPatient = useOpenPatient();
  const signalment = signalmentOf(toPatientSignalment(patient));
  const live = visits.filter(
    (visit) => visit.patient_uuid === patient.uuid && !visit.cancelled_at,
  );
  const lastSeen = live
    .filter((visit) => new Date(visitTime(visit)).getTime() < now)
    .sort((a, b) => visitTime(b).localeCompare(visitTime(a)))[0];
  const next = live
    .filter(
      (visit) =>
        visit.scheduled_start &&
        new Date(visit.scheduled_start).getTime() >= now,
    )
    .sort((a, b) => a.scheduled_start!.localeCompare(b.scheduled_start!))[0];
  return (
    <button
      type="button"
      onClick={() => openPatient(patient.uuid)}
      className="cursor-pointer rounded-paper border border-rule p-2.5 text-left transition duration-150 ease-paper hover:bg-smudge focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <span className="flex items-center gap-2">
        <PatientSprite sprite={patient.sprite} size={28} />
        <span className="min-w-0">
          <span className="block truncate font-display text-base font-semibold text-ink">
            {patient.name}
          </span>
          <span className="block truncate text-fine-print text-annotation">
            {[signalment.breed ?? signalment.species, signalment.age]
              .filter(Boolean)
              .join(" · ") || "Signalment not recorded"}
          </span>
        </span>
      </span>
      <span className="mt-2 block space-y-0.5 border-t border-dotted border-rule-strong pt-2">
        {patient.deceased_at ? (
          <PetFact label="Deceased">
            {formatMonthDay(patient.deceased_at)}
          </PetFact>
        ) : (
          <>
            <PetFact label="Weight">{weightLine(weights)}</PetFact>
            <PetFact label="Last seen">
              {lastSeen && formatMonthDay(visitTime(lastSeen))}
            </PetFact>
            <PetFact label="Next">
              {next && soonLabel(next.scheduled_start!, now)}
            </PetFact>
          </>
        )}
      </span>
    </button>
  );
};

const Pets = ({ data, now }: { data: CallContext; now: number }) => {
  const principal = usePrincipalKey();
  const shown = [...data.patients]
    .sort((a, b) => Number(!!a.deceased_at) - Number(!!b.deceased_at))
    .slice(0, PET_LIMIT);
  const weights = useQueries({
    queries: shown.map((patient) => ({
      queryKey: ["call-patient-weight", principal, patient.uuid],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        request(() =>
          apiClient.GET("/measurements", {
            params: {
              query: {
                entity_uuid: patient.uuid,
                kind: "weight",
                resolved: true,
                limit: 2,
              },
            },
            signal,
          }),
        ),
      enabled: !patient.deceased_at,
      staleTime: 60_000,
    })),
  });
  if (shown.length === 0) return null;
  const hidden = data.patients.length - shown.length;
  return (
    <>
      <Heading>Pets</Heading>
      <div className="grid grid-cols-2 gap-2">
        {shown.map((patient, index) => (
          <PetCard
            key={patient.uuid}
            patient={patient}
            visits={data.visits}
            weights={weights[index]?.data}
            now={now}
          />
        ))}
      </div>
      {(hidden > 0 || data.truncated.includes("patients")) && (
        <p className="mt-1 text-fine-print text-annotation">
          More pets on file. Open a pet to see the full record.
        </p>
      )}
    </>
  );
};

type PriorCall = CallContext["prior_calls"][number];

const BURST_MS = 15 * 60_000;

const callWeight = (call: PriorCall) =>
  call.message
    ? 3
    : call.disposition === "message_taken" || call.disposition === "handed_off"
      ? 2
      : 1;

/** Redials and pick-up legs land minutes apart; one row per burst, represented by its most informative call. */
const callBursts = (calls: PriorCall[]) => {
  const ended = calls
    .filter((call) => call.status === "ended")
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  const bursts: { call: PriorCall; count: number; oldest: number }[] = [];
  for (const call of ended) {
    const at = new Date(call.created_at).getTime();
    const last = bursts[bursts.length - 1];
    if (last && last.oldest - at <= BURST_MS) {
      last.count += 1;
      last.oldest = at;
      if (callWeight(call) > callWeight(last.call)) last.call = call;
    } else bursts.push({ call, count: 1, oldest: at });
  }
  return bursts;
};

interface Entry {
  key: string;
  at: string;
  node: ReactNode;
}

const History = ({ data, now }: { data: CallContext; now: number }) => {
  const openPatient = useOpenPatient();
  const openCall = useOpenCall();
  const entries: Entry[] = [
    ...data.visits.map((visit) => ({
      key: visit.uuid,
      at: visitTime(visit),
      node: (
        <HistoryItem
          key={visit.uuid}
          kind="visit"
          onSelect={() => openPatient(visit.patient_uuid)}
          date={visitTime(visit)}
          title={`${visit.patient_name} · ${visit.reason ?? visit.visit_type}`}
        >
          {[
            visit.cancelled_at
              ? "Cancelled"
              : new Date(visitTime(visit)).getTime() >= now
                ? "Upcoming"
                : null,
            visit.assigned_user_name,
            visit.current_stage?.name,
          ]
            .filter(Boolean)
            .join(" · ") || null}
        </HistoryItem>
      ),
    })),
    ...callBursts(data.prior_calls).map(({ call, count }) => ({
      key: call.uuid,
      at: call.created_at,
      node: (
        <HistoryItem
          key={call.uuid}
          kind="message"
          onSelect={() => openCall(call.uuid)}
          typeLabel="Call"
          date={call.created_at}
          title={
            call.origin === "inbound"
              ? phoneCallOutcome(call.status, call.disposition).label
              : "Outgoing call"
          }
        >
          {count > 1 && `${count} calls`}
          {call.message && (
            <span className="line-clamp-2">“{call.message}”</span>
          )}
        </HistoryItem>
      ),
    })),
    ...data.invoices.flatMap((invoice) =>
      invoice.issued_at
        ? [
            {
              key: invoice.uuid,
              at: invoice.issued_at,
              node: (
                <HistoryItem
                  key={invoice.uuid}
                  kind="invoice"
                  date={invoice.issued_at}
                  title={
                    invoice.lines[0]?.description ??
                    invoice.kind.replace(/_/g, " ")
                  }
                >
                  {[
                    invoice.total_amount != null &&
                      formatMoney(invoice.total_amount, invoice.currency),
                    invoice.settlement_status,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </HistoryItem>
              ),
            },
          ]
        : [],
    ),
  ]
    .sort((a, b) => b.at.localeCompare(a.at))
    .slice(0, HISTORY_LIMIT);
  if (entries.length === 0) return null;
  return (
    <>
      <Heading>History</Heading>
      <HistoryRail aria-label="Caller history" className="ml-2">
        {entries.map((entry) => entry.node)}
      </HistoryRail>
    </>
  );
};

const Notes = ({ data }: { data: CallContext }) => {
  const [latest] = data.memos;
  if (!latest) return null;
  return (
    <StickyNote className="mt-3 min-h-0 p-2.5 pt-3">
      <p className="line-clamp-4 whitespace-pre-wrap text-sm text-ink-body">
        {latest.body}
      </p>
      <p className="mt-1 text-fine-print text-annotation">
        {formatMonthDay(latest.created_at)}
        {data.memos.length > 1 && ` · ${data.memos.length - 1} more notes`}
      </p>
    </StickyNote>
  );
};

/** Ringing preview for a uniquely matched or staff-selected client; anything else keeps the full context panel. */
export const CallerPreview = ({
  context,
  fromNumber,
}: {
  context: ContextQuery;
  fromNumber: string | null;
}) => {
  const now = useNow();
  const data = context.data;
  if (context.isPending)
    return (
      <div className="mt-3">
        <SkeletonStack count={3} />
      </div>
    );
  if (!data?.client) return <CallContextPanel context={context} />;
  const matched = data.phone_numbers.find(
    (number) => lastDigits(number.number_e164) === lastDigits(fromNumber),
  );
  return (
    <div
      data-dd-privacy="hidden"
      data-testid="caller-preview"
      className="mt-1 text-ink-body"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-fine-print text-annotation">
          {data.match_source === "selection"
            ? SELECTED_CLIENT_NOTE
            : [matched?.label, "Saved number"].filter(Boolean).join(" · ")}
        </p>
        {context.selectedClientUuid && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => context.selectClient(null)}
          >
            Clear selection
          </Button>
        )}
      </div>
      <div className="mt-3">
        <OpenItems data={data} now={now} />
        <Pets data={data} now={now} />
        <History data={data} now={now} />
        <Notes data={data} />
      </div>
    </div>
  );
};
